import { PlusOutlined } from "@ant-design/icons";
import { Button, Spin, Table, message } from "antd";
import { FormInstance } from "antd/lib/form";
import debounce from "lodash.debounce";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import { PageToolbar } from "../components/PageToolbar";
import entitlements from "../config/entitlements";
import { SalesPerson, Department } from "../helpers/shared-interfaces";
import {
  request,
  handleValidationErrors,
  mapAPIErrorToFormError,
  responseHandler,
} from "../helpers/utilities";
import { CREATE_SALES_PERSON } from "../gql/salespeople";
import { GET_SALES_PERSON_DEPARTMENTS } from "../gql/salespeople";
import { AxiosResponse } from "axios";
import { GET_SALES_PEOPLE } from "../gql/users";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { ApartmentOutlined } from "@ant-design/icons";
import { generateDisplayErrorMessage } from "../helpers/utilities";



interface State {
  salesPersons: SalesPerson[];
  page: number;
  salesPeopleCount: number;
  salesPersonSearchTerm: string;
  selectedDepartments: string[];
  name: string;
  isLoading: boolean;
  showNewSalesPersonModal: boolean;
  departments: Pick<Department, "name">[];
  isCreatingSalesPerson: boolean;
  isDepartmentsLoading: boolean;
}

interface SalesPeopleResponse {
  data: {
    salesPeople: {
      results: SalesPerson[];
      count: number;
      errors: string[];
    };
  };
}

export const columns = [
  {
    title: "Sales Person Name",
    dataIndex: "name",
    key: "name",
    width: "65%",
    render: (text: string, record: SalesPerson) => (
      <NavLink
        to={`/sales-people/${record.id}`}
        data-cy="test-user-details-link"
      >
        {text}
      </NavLink>
    ),
  },
  {
    title: "Sales Person Code",
    dataIndex: "code",
    key: "code",
    width: "35%",
  },
];

export function SalesPeopleTableFactory(
  data: SalesPerson[],
  isLoading: boolean,
  page: number,
  count: number,
  nextPage: (page: number) => void,
): JSX.Element {
  return (
    <Table
      loading={isLoading}
      rowKey="id"
      columns={columns}
      dataSource={data}
      pagination={{
        current: page,
        total: count,
        defaultPageSize: 25,
        onChange: nextPage,
      }}
    />
  );
}

export default class AllSalesPeople extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      salesPersonSearchTerm: "",
      selectedDepartments: [],
      isLoading: true,
      name: "",
      salesPersons: [],
      page: 1,
      salesPeopleCount: 0,
      showNewSalesPersonModal: false,
      isCreatingSalesPerson: false,
      departments: [],
      isDepartmentsLoading: false,
    };
    this.reference = React.createRef<FormInstance>();
    this.getDepartments = this.getDepartments.bind(this);
  }

  getAllSalesPerson(): void {
    const { salesPersonSearchTerm } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_SALES_PEOPLE,
      variables: {
        page: this.state.page,
        q: salesPersonSearchTerm,
        department: this.state.selectedDepartments.length > 0 ? this.state.selectedDepartments.join(",") : undefined,
      },
    })
      .then(({ data: { data } }: any): void => {
        this.setState({
          salesPersons: data.salesPeople.results,
          salesPeopleCount: data.salesPeople.count,
        });
      })
      .catch((error) => {
        message.error("Failed to load sales people");
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  getAllDepartments = (): Promise<AxiosResponse> => {
    return request({
      query: GET_SALES_PERSON_DEPARTMENTS,
    });
  };

  async getDepartments(): Promise<void> {
    if (this.state.departments.length > 0) return;

    this.setState({ isDepartmentsLoading: true });
    try {
      const response = await this.getAllDepartments();
      const department_names: string[] =
        response.data.data.salesPersonDepartments;
      const formatted: Pick<Department, "name">[] = department_names.map(
        (name) => ({
          name: name,
        }),
      );

      this.setState({
        departments: formatted,
        isDepartmentsLoading: false,
      });
    } catch (e) {
      this.setState({ isDepartmentsLoading: false });
      message.error(`Failed to load departments - ${generateDisplayErrorMessage(e)}`);
    }
  }

  triggerDelayedSearch = debounce(() => {
    this.setState({ page: 1 }, this.getAllSalesPerson);
  }, DEBOUNCE_TIME);

  filterSalesPeople = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState(
      {
        salesPersonSearchTerm: e.target.value,
        isLoading: true,
        selectedDepartments: [],
      },
      this.triggerDelayedSearch,
    );
  };

  filterByDepartment = (values: string[]): void => {
    this.setState(
      {
        selectedDepartments: values,
        isLoading: true,
      },
      () => {
        this.triggerDelayedSearch();
      },
    );
  };

  onClear = (): void => {
    this.setState(
      {
        salesPersonSearchTerm: "",
        selectedDepartments: [],
        page: 1,
      },
      () => {
        this.getAllSalesPerson();
      },
    );
  };

  componentDidMount(): void {
    this.getAllSalesPerson();
    this.getDepartments();
  }

  nextPage = (page: number): void => {
    this.setState({ isLoading: true, page }, this.getAllSalesPerson);
  };

  toggleCreateSalesPersonModal = (): void => {
    this.setState(
      (prevState) => ({
        showNewSalesPersonModal: !prevState.showNewSalesPersonModal,
      }),
      () => {
        if (this.state.showNewSalesPersonModal) {
          this.getDepartments();
        } else {
          this.reference.current?.resetFields();
        }
      },
    );
  };

  createSalesPerson = async (): Promise<void> => {
    if (!this.reference.current) return;

    try {
      const values = await this.reference.current.validateFields();
      this.setState({ isCreatingSalesPerson: true });

      const response = await request({
        query: CREATE_SALES_PERSON,
        variables: values,
      });

      const { err, handled } = responseHandler(response, "createSalesPerson");

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
      } else if (err) {
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Sales Person created successfully");
        this.setState(
          {
            showNewSalesPersonModal: false,
            page: 1,
            salesPersonSearchTerm: "",
          },
          this.getAllSalesPerson,
        );
      }
    } catch (error) {
      handleValidationErrors(error);
    } finally {
      this.setState({ isCreatingSalesPerson: false });
    }
  };

  render(): JSX.Element {
    const {
      salesPersons,
      isLoading,
      page,
      salesPeopleCount,
      showNewSalesPersonModal,
      isCreatingSalesPerson,
      departments,
      salesPersonSearchTerm,
    } = this.state;

    return (
      <>
        <Card>
          <div className="departments-table-container">
            <PageToolbar modelName="salespeople"
              searchPlaceholder="Filter by name..."
              searchValue={salesPersonSearchTerm}
              onSearchChange={this.filterSalesPeople}
              filters={
                <PageToolbar.Filters
                  isFiltering={!!(salesPersonSearchTerm || this.state.selectedDepartments.length > 0)}
                  onClear={this.onClear}
                  configs={[
                    {
                      label: "Department",
                      options: departments.map((dept) => ({ value: dept.name, label: dept.name })),
                      selectedValues: this.state.selectedDepartments,
                      onChange: this.filterByDepartment,
                      placeholder: "Filter by department",
                      icon: <ApartmentOutlined />
                    }
                  ]}
                />
              }
              primaryAction={
                <EntitledSection entitlement={entitlements.Users.create}>
                  <Button
                    type="primary"
                    onClick={this.toggleCreateSalesPersonModal}
                  >
                    <PlusOutlined /> Create Sales Person
                  </Button>
                </EntitledSection>
              }
              totalCount={salesPeopleCount}
              currentDataCount={salesPersons.length}
              page={page}
              pageSize={25}
            />

            {SalesPeopleTableFactory(
              salesPersons,
              isLoading,
              page,
              salesPeopleCount,
              this.nextPage,
            )}

            {showNewSalesPersonModal &&
              ModalCreator({
                title: "Create Sales Person",
                okText: "Create",
                okType: "primary",
                showModal: showNewSalesPersonModal,
                onOkay: this.createSalesPerson,
                toggleModal: this.toggleCreateSalesPersonModal,
                inscription: (
                  <Spin
                    spinning={
                      isCreatingSalesPerson || this.state.isDepartmentsLoading
                    }
                  >
                    <BaseForm
                      layout="vertical"
                      reference={this.reference}
                      fieldsToHide={[]}
                      fields={[
                        {
                          name: "name",
                          label: "Name",
                          inputType: "string",
                          placeholder: "Enter name",
                          required: true,
                          colConfig: { span: 22 },
                        },
                        {
                          name: "department",
                          label: "Department",
                          selectOptions: departments.map((dept) => ({
                            id: dept.name,
                            name: dept.name,
                          })),
                          inputType: "select",
                          placeholder: "Select department",
                          required: true,
                          colConfig: { span: 22 },
                        },
                      ]}
                    />
                  </Spin>
                ),
              })}
          </div>
        </Card>
      </>
    );
  }
}
